/* Sprint 2Q — B2B Customer ESG report page.
 *
 * Customers can download their monthly documented CO2e report (PDF / Excel).
 * Compliance-safe: NO greenwashing claims.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Download, FileText, ShieldCheck, BarChart3,
  CheckCircle2, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function portalApi() {
  const token = localStorage.getItem("ema_business_token");
  return axios.create({
    baseURL: API,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 100);
}

export default function BusinessEsgReportsPage() {
  const ctx = useOutletContext() || {};
  const customer = ctx.customer || {};

  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadPeriods = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await portalApi().get("/portal/esg/reports");
      setPeriods(data.items || []);
      if ((data.items || []).length > 0) {
        setSelectedPeriod(data.items[0].period);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke hente ESG-rapporter");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  useEffect(() => {
    if (!selectedPeriod) { setPreview(null); return; }
    let cancelled = false;
    (async () => {
      setPreviewLoading(true);
      try {
        const { data } = await portalApi().get(`/portal/esg/reports/${selectedPeriod}.json`);
        if (!cancelled) setPreview(data);
      } catch (e) {
        if (!cancelled) toast.error("Forhåndsvisning fejlede");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPeriod]);

  const downloadReport = async (kind) => {
    if (!selectedPeriod) return;
    try {
      const r = await portalApi().get(
        `/portal/esg/reports/${selectedPeriod}.${kind}`,
        { responseType: "blob" },
      );
      downloadBlob(r.data, `EMA-ESG_${selectedPeriod}.${kind === "pdf" ? "pdf" : "xlsx"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download fejlede");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="esg-portal-loading">
        <Loader2 className="animate-spin text-brand-red" size={28}/>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="business-esg-page">
      <div>
        <div className="text-xs uppercase tracking-wider text-brand-red font-semibold">
          ESG-rapporter
        </div>
        <h1 className="font-display font-bold text-3xl text-brand-dark mt-1">
          Audit-klar ESG-rapport
        </h1>
        <p className="text-sm text-brand-text2 mt-1">
          Dokumenteret CO2e-beregning for <strong>{customer.company_name}</strong>.
        </p>
      </div>

      {/* Compliance banner */}
      <div className="bg-amber-50 border-l-4 border-amber-500 p-4 text-sm text-amber-900 flex items-start gap-2" data-testid="esg-portal-compliance">
        <ShieldCheck size={18} className="mt-0.5 shrink-0"/>
        <div>
          <div className="font-bold mb-1">Forbehold (Compliance)</div>
          <p>
            Tallene er <strong>operationelt dokumenterede estimater</strong>
            beregnet med EMA Kurer-metodologi. De er ikke eksternt verificerede
            medmindre verifikationsdokument er vedhæftet.
          </p>
        </div>
      </div>

      {periods.length === 0 ? (
        <div className="bg-white border border-brand p-6 text-center text-brand-text2" data-testid="esg-portal-empty">
          Ingen ESG-data registreret endnu. Når jeres forsendelser er gennemført
          og afsluttede, vil rapporter være tilgængelige her.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Period list */}
          <div className="bg-white border border-brand p-3 lg:col-span-1" data-testid="esg-portal-period-list">
            <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2">
              Vælg periode
            </div>
            <ul className="space-y-1">
              {periods.map((p) => (
                <li key={p.period}>
                  <button
                    onClick={() => setSelectedPeriod(p.period)}
                    data-testid={`esg-portal-period-${p.period}`}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${
                      selectedPeriod === p.period
                        ? "bg-brand-dark text-white"
                        : "bg-brand-bg2 text-brand-dark hover:bg-brand-bg2/70"
                    }`}
                  >
                    <span className="font-mono">{p.period}</span>
                    <span className="text-xs">
                      {p.shipments} fors. · {(p.co2e_kg || 0).toFixed(2)} kg
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Preview + downloads */}
          <div className="lg:col-span-2 space-y-3" data-testid="esg-portal-preview-pane">
            {previewLoading && (
              <div className="bg-white border border-brand p-6 flex items-center justify-center">
                <Loader2 className="animate-spin text-brand-red" size={20}/>
              </div>
            )}
            {!previewLoading && preview && (
              <>
                <div className="bg-white border border-brand p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-[11px] uppercase tracking-widest text-brand-text2 font-bold">Periode</div>
                      <div className="font-display font-bold text-xl text-brand-dark">{preview.period}</div>
                    </div>
                    <span className="text-[11px] bg-brand-bg2 border border-brand px-2 py-1 font-mono">
                      Metode {preview.method_version}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
                    <MiniKpi label="Forsendelser" value={preview.shipment_count} testid="esg-kpi-shipments"/>
                    <MiniKpi label="CO2e i alt (kg)"
                              value={preview.co2e_total_kg.toFixed(3)}
                              testid="esg-kpi-co2e"/>
                    <MiniKpi label="CO2e/forsendelse (kg)"
                              value={preview.co2e_per_shipment_kg.toFixed(4)}
                              testid="esg-kpi-per-shipment"/>
                    <MiniKpi label="Leveringsrate"
                              value={`${preview.delivery_success_rate_pct.toFixed(1)} %`}
                              testid="esg-kpi-success"/>
                    <MiniKpi label="Kørte km"
                              value={preview.total_km_used.toFixed(1)}
                              testid="esg-kpi-km"/>
                    <MiniKpi label="Konfidens"
                              value={preview.confidence_level}
                              testid="esg-kpi-confidence"/>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-4 text-xs">
                    <div className="bg-brand-bg2 p-2 border border-brand">
                      <div className="text-brand-text2 uppercase tracking-wider text-[10px]">Same Day</div>
                      <div className="font-bold text-brand-dark">{preview.service_level_split.same_day}</div>
                    </div>
                    <div className="bg-brand-bg2 p-2 border border-brand">
                      <div className="text-brand-text2 uppercase tracking-wider text-[10px]">Next Day</div>
                      <div className="font-bold text-brand-dark">{preview.service_level_split.next_day}</div>
                    </div>
                    <div className="bg-brand-bg2 p-2 border border-brand">
                      <div className="text-brand-text2 uppercase tracking-wider text-[10px]">Andet</div>
                      <div className="font-bold text-brand-dark">{preview.service_level_split.other}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                    <div className="bg-white p-2 border border-brand">
                      <div className="text-brand-text2 uppercase tracking-wider text-[10px]">Fejl</div>
                      <div className="font-bold text-red-700">{preview.failed_delivery_count}</div>
                    </div>
                    <div className="bg-white p-2 border border-brand">
                      <div className="text-brand-text2 uppercase tracking-wider text-[10px]">Genleveringer</div>
                      <div className="font-bold text-amber-700">{preview.redelivery_count}</div>
                    </div>
                    <div className="bg-white p-2 border border-brand">
                      <div className="text-brand-text2 uppercase tracking-wider text-[10px]">Returer</div>
                      <div className="font-bold text-brand-dark">{preview.return_count}</div>
                    </div>
                  </div>

                  {/* Verified indicator */}
                  <div className="mt-4 flex items-center gap-2 text-xs">
                    {preview.externally_verified ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 border border-green-200 font-semibold">
                        <CheckCircle2 size={12}/> Eksternt verificeret
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-brand-bg2 text-brand-text2 border border-brand">
                        <AlertTriangle size={12}/> Ikke eksternt verificeret
                      </span>
                    )}
                  </div>
                </div>

                {/* Download buttons */}
                <div className="bg-white border border-brand p-4">
                  <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-3 flex items-center gap-2">
                    <Download size={14}/> Download rapport
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => downloadReport("pdf")}
                      className="px-4 py-2.5 bg-brand-red text-white font-bold text-sm uppercase tracking-wider inline-flex items-center gap-1.5"
                      data-testid="esg-download-pdf"
                    >
                      <FileText size={14}/> PDF
                    </button>
                    <button
                      onClick={() => downloadReport("xlsx")}
                      className="px-4 py-2.5 bg-brand-dark text-white font-bold text-sm uppercase tracking-wider inline-flex items-center gap-1.5"
                      data-testid="esg-download-xlsx"
                    >
                      <BarChart3 size={14}/> Excel (m. ledger)
                    </button>
                  </div>
                  <p className="text-[11px] text-brand-text2 italic mt-3">{preview.disclaimer}</p>
                </div>

                {/* Methodology */}
                <div className="bg-white border border-brand p-4">
                  <div className="text-xs uppercase tracking-widest text-brand-text2 font-bold mb-2">
                    Beregnings-metode
                  </div>
                  <p className="text-sm text-brand-text2 leading-relaxed">
                    {preview.method_description}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MiniKpi({ label, value, testid }) {
  return (
    <div className="bg-brand-bg2 border border-brand p-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-brand-text2 font-semibold">{label}</div>
      <div className="font-display font-bold text-base text-brand-dark mt-0.5">{value}</div>
    </div>
  );
}
